import { ChevronLeft, ChevronRight } from "lucide-react";
import type React from "react";
import {
  createContext,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useState,
} from "react";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/v2/constants/testIds";
import { Sheet, SheetContent } from "@/v2/ui/primitives/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/v2/ui/primitives/tooltip";

const SIDEBAR_COLLAPSED_KEY = "sidebar-collapsed";
const TRANSITION = "tw-transition-all tw-duration-400 tw-ease-in-out";

function toSlug(name: string): string {
  return name.toLowerCase().replace(/\s+/g, "-");
}

export interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  defaultCollapsed?: boolean;
  isMobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
  isTourRunning?: boolean;
}

interface SidebarContextValue {
  /** Effective collapsed state (respects isTourRunning override) */
  collapsed: boolean;
  /** Raw collapsed state from user preference */
  rawCollapsed: boolean;
  /** Whether tour is running - used to disable transitions for instant state changes */
  isTourActive: boolean;
  setCollapsed: (collapsed: boolean) => void;
  closeMobileMenu: () => void;
}

const SidebarContext = createContext<SidebarContextValue | undefined>(
  undefined
);

const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a Sidebar");
  }
  return context;
};

const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(
  (
    {
      className,
      children,
      defaultCollapsed,
      isMobileOpen,
      onMobileOpenChange,
      isTourRunning,
      ...props
    },
    ref
  ) => {
    const [collapsed, setCollapsedState] = useState(() => {
      if (typeof defaultCollapsed === "boolean") return defaultCollapsed;
      try {
        const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
        return stored === "true";
      } catch {
        return false;
      }
    });

    const setCollapsed = useCallback((value: boolean) => {
      setCollapsedState(value);
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value));
      } catch {
        // Ignore localStorage errors
      }
    }, []);

    const closeMobileMenu = useCallback(() => {
      onMobileOpenChange?.(false);
    }, [onMobileOpenChange]);

    const sidebarContent = (
      <div
        className={cn(
          "tw-flex-1 tw-overflow-x-hidden tw-pt-1 tw-pb-3",
          "tw-overflow-y-auto", // Keep scrollable during tour so Analytics can scroll into view
          "[&::-webkit-scrollbar]:tw-w-1.5",
          "[&::-webkit-scrollbar-track]:tw-bg-transparent",
          "[&::-webkit-scrollbar-thumb]:tw-bg-border [&::-webkit-scrollbar-thumb]:tw-rounded-full",
          "[&::-webkit-scrollbar-thumb:hover]:tw-bg-muted-foreground/30"
        )}
      >
        {children}
      </div>
    );

    // Effective collapsed state: force expanded during tour
    const effectiveCollapsed = collapsed && !isTourRunning;

    // Flag for child components to disable transitions during tour
    const isTourActive = Boolean(isTourRunning);

    // Disable transitions during tour for instant state changes
    const transition = isTourActive ? "" : TRANSITION;

    return (
      <SidebarContext.Provider
        value={{
          collapsed: effectiveCollapsed,
          rawCollapsed: collapsed,
          isTourActive,
          setCollapsed,
          closeMobileMenu,
        }}
      >
        <TooltipProvider delayDuration={300}>
          <div className="tw-relative tw-group/sidebar tw-hidden lg:tw-block">
            <div
              ref={ref}
              data-testid={TEST_IDS.sidebar}
              className={cn(
                "tw-flex tw-flex-col tw-flex-shrink-0 tw-h-full",
                "tw-bg-white dark:tw-bg-slate-950",
                "tw-border-r tw-border-border",
                transition,
                // Force expanded during tour so spotlight targets are visible
                effectiveCollapsed ? "tw-w-[68px]" : "tw-w-[260px]",
                className
              )}
              {...props}
            >
              {sidebarContent}
            </div>

            <button
              type="button"
              data-tour="sidebar-toggle"
              onClick={() => setCollapsed(!collapsed)}
              className={cn(
                "tw-absolute tw-top-1/2 tw--translate-y-1/2 tw-z-50",
                "tw-flex tw-items-center tw-justify-center",
                "tw-h-6 tw-w-6 tw-rounded-full",
                "tw-bg-white dark:tw-bg-slate-950 tw-backdrop-blur-sm",
                "tw-border tw-border-border",
                "tw-shadow-md",
                "tw-text-muted-foreground hover:tw-text-foreground",
                isTourRunning
                  ? "tw-opacity-100"
                  : "tw-opacity-0 group-hover/sidebar:tw-opacity-100",
                transition,
                "hover:tw-bg-muted hover:tw-scale-110",
                "tw-right-0 tw-translate-x-1/2"
              )}
              aria-label={
                collapsed
                  ? strings("common.expandSidebar")
                  : strings("common.collapseSidebar")
              }
            >
              {collapsed ? (
                <ChevronRight className="tw-h-3.5 tw-w-3.5" />
              ) : (
                <ChevronLeft className="tw-h-3.5 tw-w-3.5" />
              )}
            </button>
          </div>

          <Sheet open={isMobileOpen} onOpenChange={onMobileOpenChange}>
            <SheetContent
              side="left"
              className="tw-p-0 tw-w-[280px] tw-bg-white dark:tw-bg-slate-950"
            >
              <SidebarContext.Provider
                value={{
                  collapsed: false,
                  rawCollapsed: collapsed,
                  isTourActive,
                  setCollapsed,
                  closeMobileMenu,
                }}
              >
                <div className="tw-flex tw-flex-col tw-h-full">
                  {sidebarContent}
                </div>
              </SidebarContext.Provider>
            </SheetContent>
          </Sheet>
        </TooltipProvider>
      </SidebarContext.Provider>
    );
  }
);

Sidebar.displayName = "Sidebar";

export interface SidebarItemProps {
  name: string;
  icon?: string;
  IconComponent?: React.ComponentType<{ className?: string }>;
  isSelected?: boolean;
  isHeader?: boolean;
  disabled?: boolean;
  route?: string;
  badge?: string;
  badgeColor?: string;
  onClick?: () => void;
  dataTour?: string;
}

const SidebarItemInner = forwardRef<HTMLAnchorElement, SidebarItemProps>(
  (
    {
      name,
      icon,
      IconComponent,
      isSelected = false,
      isHeader = false,
      disabled = false,
      route,
      badge,
      badgeColor,
      onClick,
      dataTour,
    },
    ref
  ) => {
    const { collapsed, isTourActive, closeMobileMenu } = useSidebar();
    // Disable transitions during tour for instant state changes
    const transition = isTourActive ? "" : TRANSITION;

    if (isHeader) {
      return (
        <div
          className={cn(
            "tw-flex tw-items-center tw-gap-2 tw-mx-3 tw-px-2",
            "tw-select-none tw-overflow-hidden",
            transition,
            collapsed
              ? "tw-opacity-0 tw-h-0 tw-my-0 tw-scale-y-0"
              : "tw-opacity-100 tw-h-7 tw-mb-2 first:tw-mt-2 tw-mt-5 tw-scale-y-100"
          )}
        >
          {IconComponent && (
            <IconComponent className="tw-h-3.5 tw-w-3.5 tw-text-muted-foreground tw-flex-shrink-0" />
          )}
          <span className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-muted-foreground tw-whitespace-nowrap">
            {name}
          </span>
          <div className="tw-flex-1 tw-h-px tw-bg-gradient-to-r tw-from-border tw-to-transparent tw-ml-2" />
        </div>
      );
    }

    const iconElement = (
      <>
        {icon && (
          <img
            src={icon}
            alt=""
            className={cn(
              "tw-h-[18px] tw-w-[18px] tw-flex-shrink-0",
              transition,
              disabled && "tw-opacity-40"
            )}
          />
        )}
        {IconComponent && (
          <IconComponent
            className={cn(
              "tw-h-[18px] tw-w-[18px] tw-flex-shrink-0",
              transition,
              isSelected
                ? "tw-text-primary"
                : disabled
                  ? "tw-text-muted-foreground/50"
                  : "tw-text-muted-foreground group-hover/item:tw-text-foreground"
            )}
          />
        )}
      </>
    );

    const content = (
      <>
        <span className="tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
          {iconElement}
        </span>
        <span
          className={cn(
            "tw-text-[13px] tw-font-medium tw-whitespace-nowrap",
            transition,
            collapsed
              ? "tw-opacity-0 tw-w-0 tw-translate-x-[-8px]"
              : "tw-opacity-100 tw-flex-1 tw-translate-x-0",
            isSelected
              ? "tw-text-foreground"
              : disabled
                ? "tw-text-muted-foreground/50"
                : "tw-text-muted-foreground group-hover/item:tw-text-foreground"
          )}
        >
          {name}
        </span>
        {badge && (
          <span
            className={cn(
              "tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide",
              "tw-py-0.5 tw-rounded tw-whitespace-nowrap",
              "tw-bg-primary/10 tw-text-primary",
              transition,
              collapsed
                ? "tw-opacity-0 tw-w-0 tw-px-0 tw-scale-0"
                : "tw-opacity-100 tw-px-1.5 tw-scale-100"
            )}
            style={
              badgeColor
                ? {
                    backgroundColor: `${badgeColor}20`,
                    color: badgeColor,
                  }
                : undefined
            }
          >
            {badge}
          </span>
        )}
      </>
    );

    const linkElement = (
      <a
        ref={ref}
        href={disabled ? undefined : route || "#"}
        className={cn(
          "tw-group/item tw-relative tw-flex tw-items-center",
          "tw-mx-2 tw-my-0.5 tw-rounded-lg",
          "tw-no-underline tw-outline-none",
          "tw-px-3 tw-py-2.5 tw-gap-3",
          transition,
          disabled && "tw-cursor-not-allowed tw-pointer-events-none",
          !disabled && !isSelected && "tw-cursor-pointer",
          isSelected && [
            "tw-bg-primary/8",
            "tw-border tw-border-primary/15",
            "before:tw-absolute before:tw-left-0 before:tw-top-1/2 before:tw--translate-y-1/2",
            "before:tw-w-[3px] before:tw-h-5 before:tw-rounded-full",
            "before:tw-bg-primary",
          ],
          !disabled &&
            !isSelected && [
              "hover:tw-bg-muted",
              "tw-border tw-border-transparent",
              "hover:tw-border-border",
            ]
        )}
        data-testid={TEST_IDS.sidebarNavItem(toSlug(name))}
        data-tour={dataTour}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          if (onClick) {
            e.preventDefault();
            onClick();
          }
          closeMobileMenu();
        }}
        aria-disabled={disabled}
      >
        {content}
      </a>
    );

    // Show tooltip when collapsed
    if (collapsed) {
      return (
        <Tooltip>
          <TooltipTrigger asChild={true}>{linkElement}</TooltipTrigger>
          <TooltipContent side="right" sideOffset={12}>
            <p className="tw-font-medium tw-text-sm">{name}</p>
            {badge && (
              <p className="tw-text-xs tw-text-primary tw-mt-0.5">{badge}</p>
            )}
          </TooltipContent>
        </Tooltip>
      );
    }

    return linkElement;
  }
);

SidebarItemInner.displayName = "SidebarItem";

const SidebarItem = memo(SidebarItemInner) as React.MemoExoticComponent<
  React.ForwardRefExoticComponent<
    SidebarItemProps & React.RefAttributes<HTMLAnchorElement>
  >
>;

export interface SidebarExternalLinkProps {
  name: string;
  description?: string;
  href: string;
  icon?: string;
  IconComponent?: React.ComponentType<{ className?: string }>;
  ExternalIcon?: React.ComponentType<{ className?: string }>;
}

const SidebarExternalLinkInner = forwardRef<
  HTMLAnchorElement,
  SidebarExternalLinkProps
>(({ name, description, href, icon, IconComponent, ExternalIcon }, ref) => {
  const { collapsed, isTourActive } = useSidebar();
  const transition = isTourActive ? "" : TRANSITION;

  const linkElement = (
    <a
      ref={ref}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "tw-group/link tw-relative tw-flex tw-items-center",
        "tw-mx-2 tw-my-0.5 tw-rounded-lg",
        "tw-no-underline tw-outline-none tw-cursor-pointer",
        "tw-border tw-border-transparent",
        "hover:tw-bg-muted hover:tw-border-border",
        "tw-px-3 tw-py-2.5 tw-gap-3",
        transition
      )}
    >
      {icon && (
        <img
          src={icon}
          alt=""
          className={cn(
            "tw-h-[18px] tw-w-[18px] tw-flex-shrink-0",
            "tw-opacity-70 group-hover/link:tw-opacity-100",
            transition
          )}
        />
      )}
      {IconComponent && (
        <IconComponent
          className={cn(
            "tw-h-[18px] tw-w-[18px] tw-flex-shrink-0",
            "tw-text-muted-foreground group-hover/link:tw-text-foreground",
            transition
          )}
        />
      )}
      <div
        className={cn(
          "tw-min-w-0 tw-overflow-hidden",
          transition,
          collapsed
            ? "tw-opacity-0 tw-w-0 tw-translate-x-[-8px]"
            : "tw-opacity-100 tw-flex-1 tw-translate-x-0"
        )}
      >
        <p className="tw-m-0 tw-text-[13px] tw-font-medium tw-text-muted-foreground group-hover/link:tw-text-foreground tw-truncate tw-whitespace-nowrap">
          {name}
        </p>
        {description && (
          <p className="tw-m-0 tw-text-[11px] tw-text-muted-foreground/70 tw-truncate tw-whitespace-nowrap">
            {description}
          </p>
        )}
      </div>
      {ExternalIcon && (
        <ExternalIcon
          className={cn(
            "tw-text-muted-foreground group-hover/link:tw-text-foreground tw-flex-shrink-0",
            "group-hover/link:tw-translate-x-0.5 group-hover/link:tw--translate-y-0.5",
            transition,
            collapsed
              ? "tw-opacity-0 tw-w-0 tw-h-0 tw-scale-0"
              : "tw-opacity-100 tw-w-3.5 tw-h-3.5 tw-scale-100"
          )}
        />
      )}
    </a>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild={true}>{linkElement}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={12}>
          <p className="tw-font-medium tw-text-sm">{name}</p>
          {description && (
            <p className="tw-text-xs tw-text-muted-foreground tw-mt-0.5">
              {description}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    );
  }

  return linkElement;
});

SidebarExternalLinkInner.displayName = "SidebarExternalLink";

const SidebarExternalLink = memo(
  SidebarExternalLinkInner
) as React.MemoExoticComponent<
  React.ForwardRefExoticComponent<
    SidebarExternalLinkProps & React.RefAttributes<HTMLAnchorElement>
  >
>;

export const SidebarDivider = () => {
  const { collapsed, isTourActive } = useSidebar();
  const transition = isTourActive ? "" : TRANSITION;

  return (
    <div
      className={cn("tw-my-2", transition, collapsed ? "tw-mx-3" : "tw-mx-4")}
    >
      <div className="tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-border tw-to-transparent" />
    </div>
  );
};

const SidebarFooter = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const { collapsed, isTourActive } = useSidebar();
  const transition = isTourActive ? "" : TRANSITION;

  return (
    <div
      ref={ref}
      className={cn(
        "tw-mt-auto tw-overflow-hidden",
        "tw-border-t tw-border-border",
        transition,
        collapsed
          ? "tw-opacity-0 tw-max-h-0 tw-p-0 tw-border-t-transparent tw-scale-y-0"
          : "tw-opacity-100 tw-max-h-32 tw-p-3 tw-scale-y-100",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});

SidebarFooter.displayName = "SidebarFooter";

export { Sidebar, SidebarItem, SidebarExternalLink, SidebarFooter, useSidebar };
